import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, Cell, PieChart, Pie,
  AreaChart, Area, ScatterChart, Scatter, ZAxis,
} from 'recharts';
import { HiOutlineArrowPath, HiOutlineChartBar, HiOutlineTableCells } from 'react-icons/hi2';
import { authService, machineService, oeeService, masterDataService, plantService, capaService, reportService } from '../../services';
import apiClient from '../../config/api';
import { SEMANTIC_SUCCESS_MID } from '../../theme/chartColors';
import PageLoading from '../../components/common/PageLoading';
import './CorporateDashboard.css';

const COLORS = ['#6366f1', '#0ea5e9', SEMANTIC_SUCCESS_MID, '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4'];
const oeeColor = (v) => v >= 85 ? '#16a34a' : v >= 60 ? '#eab308' : '#dc2626';

const CorporateDashboard = () => {
  /* ──── state ──── */
  const [plants, setPlants] = useState([]);
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [viewMode, setViewMode] = useState('chart'); // 'chart' | 'table'
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  });
  const [granularity, setGranularity] = useState('daily');

  /* data */
  const [plantOeeData, setPlantOeeData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [downtimeReport, setDowntimeReport] = useState([]);
  const [mtbfData, setMtbfData] = useState([]);
  const [mtbrData, setMtbrData] = useState([]);
  const [capaCount, setCapaCount] = useState(0);
  const [allMachineMetrics, setAllMachineMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const tenantId = authService.getTenantId();

  useEffect(() => { loadInitialData(); }, []);

  useEffect(() => {
    if (machines.length > 0 && shifts.length > 0) {
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedPlants, plants, machines, shifts, granularity]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [plantsData, machinesData, shiftsData] = await Promise.all([
        plantService.getAllPlants(),
        machineService.getAllMachines(),
        masterDataService.getShifts(tenantId),
      ]);
      // Don't filter by status — show ALL plants
      setPlants(plantsData || []);
      setMachines(machinesData || []);
      setShifts(shiftsData || []);

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
          setSelectedPlants([plantsData[0].plantId]);
      }
    } catch (err) {
      console.error('Failed to load initial data', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    setRefreshing(true);
    const activePlants = selectedPlants.length > 0
      ? plants.filter(p => selectedPlants.includes(p.plantId))
      : plants;

    const defaultShiftId = shifts.length > 0 ? shifts[0].shiftId : null;
    if (!defaultShiftId) { setRefreshing(false); return; }

    const allMachinesToUse = selectedPlants.length > 0
      ? machines.filter(m => selectedPlants.includes(m.plantId))
      : machines;

    /* ── 1. Per-machine metrics (used for plant aggregation + worst machines) ── */
    const machineMetrics = [];
    await Promise.all(
      allMachinesToUse.map(async (m) => {
        try {
          const met = await oeeService.getShiftMetrics(m.machineId, defaultShiftId, dateRange.end);
          if (met) {
            machineMetrics.push({
              machineId: m.machineId,
              plantId: m.plantId,
              name: m.machineName || m.machineCode,
              code: m.machineCode,
              oee: +(met.oee || 0).toFixed(1),
              availability: +(met.availability || 0).toFixed(1),
              performance: +(met.performance || 0).toFixed(1),
              quality: +(met.quality || 0).toFixed(1),
              produced: met.totalProduced || 0,
              rejects: met.totalRejects || 0,
              rework: met.totalRework || 0,
              plannedDT: +(met.totalPlannedDowntimeMinutes || 0).toFixed(1),
              unplannedDT: +(met.totalUnplannedDowntimeMinutes || 0).toFixed(1),
              totalDT: +((met.totalPlannedDowntimeMinutes || 0) + (met.totalUnplannedDowntimeMinutes || 0)).toFixed(1),
            });
          }
        } catch { /* skip */ }
      }),
    );
    setAllMachineMetrics(machineMetrics);

    /* ── 2. Plant-wise aggregation ── */
    const plantResults = activePlants.map(plant => {
      const pm = machineMetrics.filter(m => m.plantId === plant.plantId);
      const count = pm.length;
      return {
        plantId: plant.plantId,
        name: plant.plantName || plant.plantCode || `Plant ${plant.plantId}`,
        oee: count > 0 ? +(pm.reduce((s, m) => s + m.oee, 0) / count).toFixed(1) : 0,
        availability: count > 0 ? +(pm.reduce((s, m) => s + m.availability, 0) / count).toFixed(1) : 0,
        performance: count > 0 ? +(pm.reduce((s, m) => s + m.performance, 0) / count).toFixed(1) : 0,
        quality: count > 0 ? +(pm.reduce((s, m) => s + m.quality, 0) / count).toFixed(1) : 0,
        produced: pm.reduce((s, m) => s + m.produced, 0),
        rejects: pm.reduce((s, m) => s + m.rejects, 0),
        rework: pm.reduce((s, m) => s + m.rework, 0),
        machineCount: machines.filter(mc => mc.plantId === plant.plantId).length,
      };
    });
    setPlantOeeData(plantResults);

    /* ── 3. OEE trend ── */
    try {
      const days = [];
      const dStart = new Date(dateRange.start);
      const dEnd = new Date(dateRange.end);
      const step = granularity === 'weekly' ? 7 : granularity === 'monthly' ? 30 : 5;
      for (let d = new Date(dStart); d <= dEnd; d.setDate(d.getDate() + step)) {
        days.push(new Date(d).toISOString().slice(0, 10));
      }
      if (!days.includes(dateRange.end)) days.push(dateRange.end);

      const sampled = allMachinesToUse.slice(0, 10); // limit for performance
      const trendResults = await Promise.all(
        days.map(async (day) => {
          let oeeSum = 0, count = 0;
          await Promise.all(
            sampled.map(async (m) => {
              try {
                const met = await oeeService.getShiftMetrics(m.machineId, defaultShiftId, day);
                if (met?.oee != null) { oeeSum += met.oee; count++; }
              } catch { /* ignore */ }
            }),
          );
          return { date: day.slice(5), oee: count > 0 ? +(oeeSum / count).toFixed(1) : 0 };
        }),
      );
      setTrendData(trendResults);
    } catch { setTrendData([]); }

    /* ── 4. Downtime report ── */
    try {
      const dt = await reportService.getDowntimeReport(new Date(dateRange.start), new Date(dateRange.end));
      setDowntimeReport(dt || []);
    } catch { setDowntimeReport([]); }

    /* ── 5. MTBF / MTBR ── */
    try { setMtbfData(await reportService.getMTBFReport(new Date(dateRange.start), new Date(dateRange.end)) || []); } catch { setMtbfData([]); }
    try { setMtbrData(await reportService.getMTBRReport(new Date(dateRange.start), new Date(dateRange.end)) || []); } catch { setMtbrData([]); }

    /* ── 6. CAPA count ── */
    try {
      const capaRes = await capaService.getCapaRecords();
      const records = Array.isArray(capaRes) ? capaRes : (capaRes?.data || []);
      setCapaCount(records.filter(c => c.statusName?.toLowerCase() !== 'closed' && c.statusName?.toLowerCase() !== 'completed').length);
    } catch { setCapaCount(0); }

    setRefreshing(false);
  };

  /* ──── derived ──── */
  const enterpriseOee = useMemo(() => {
    if (plantOeeData.length === 0) return 0;
    return +(plantOeeData.reduce((s, p) => s + p.oee, 0) / plantOeeData.length).toFixed(1);
  }, [plantOeeData]);

  const bestPlant = useMemo(() => {
    if (plantOeeData.length === 0) return { name: '-', oee: 0 };
    return plantOeeData.reduce((a, b) => a.oee >= b.oee ? a : b, plantOeeData[0]);
  }, [plantOeeData]);
  const worstPlant = useMemo(() => {
    if (plantOeeData.length === 0) return { name: '-', oee: 0 };
    return plantOeeData.reduce((a, b) => a.oee <= b.oee ? a : b, plantOeeData[0]);
  }, [plantOeeData]);

  const totalDowntimeHrs = useMemo(() => {
    return +(allMachineMetrics.reduce((s, m) => s + (m.totalDT || 0), 0) / 60).toFixed(1);
  }, [allMachineMetrics]);

  const avgMtbf = useMemo(() => {
    if (mtbfData.length === 0) return '-';
    const vals = mtbfData.map(m => parseFloat(m['MTBF (hours)'] || 0)).filter(v => v > 0);
    return vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : '-';
  }, [mtbfData]);
  const avgMtbr = useMemo(() => {
    if (mtbrData.length === 0) return '-';
    const vals = mtbrData.map(m => parseFloat(m['MTBR (hours)'] || 0)).filter(v => v > 0);
    return vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : '-';
  }, [mtbrData]);

  /* loss breakdown from live plantOeeData */
  const lossBreakdown = useMemo(() => {
    if (plantOeeData.length === 0) return [];
    const avgA = plantOeeData.reduce((s, p) => s + p.availability, 0) / plantOeeData.length;
    const avgP = plantOeeData.reduce((s, p) => s + p.performance, 0) / plantOeeData.length;
    const avgQ = plantOeeData.reduce((s, p) => s + p.quality, 0) / plantOeeData.length;
    return [
      { name: 'Availability Loss', value: +(100 - avgA).toFixed(1), color: '#ef4444' },
      { name: 'Performance Loss', value: +(100 - avgP).toFixed(1), color: '#f59e0b' },
      { name: 'Quality Loss', value: +(100 - avgQ).toFixed(1), color: '#8b5cf6' },
    ];
  }, [plantOeeData]);

  /* downtime by machine from report */
  const downtimeByMachine = useMemo(() => {
    const map = {};
    downtimeReport.forEach(r => {
      const key = r['Machine'] || 'Unknown';
      map[key] = (map[key] || 0) + parseFloat(r['Total Downtime (min)'] || 0);
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10)
      .map(([name, value]) => ({ name, value: +value.toFixed(1) }));
  }, [downtimeReport]);

  /* production by plant */
  const productionByPlant = useMemo(() =>
    plantOeeData.map(p => ({ name: p.name, produced: p.produced, rejects: p.rejects, rework: p.rework })),
    [plantOeeData]);

  /* worst machines */
  const worstMachines = useMemo(() =>
    [...allMachineMetrics].sort((a, b) => a.oee - b.oee).slice(0, 10),
    [allMachineMetrics]);

  /* reliability scatter */
  const reliabilityData = useMemo(() => {
    const map = {};
    mtbfData.forEach(m => { const k = m['Machine'] || 'Avg'; map[k] = { ...map[k], name: k, mtbf: parseFloat(m['MTBF (hours)'] || 0) }; });
    mtbrData.forEach(m => { const k = m['Machine'] || 'Avg'; map[k] = { ...map[k], name: k, mtbr: parseFloat(m['MTBR (hours)'] || 0) }; });
    return Object.values(map).filter(d => d.mtbf > 0 || d.mtbr > 0)
      .map(d => ({ ...d, mtbf: d.mtbf || 0, mtbr: d.mtbr || 0, size: Math.max((d.mtbr || 1) * 10, 30) }));
  }, [mtbfData, mtbrData]);

  const togglePlant = (plantId) => {
    setSelectedPlants(prev => {
        const next = prev.includes(plantId) ? prev.filter(id => id !== plantId) : [...prev, plantId];
        if (next.length > 0) {
            localStorage.setItem('defaultPlantId', next[0].toString());
        }
        return next;
    });
  };

  if (loading) {
    return (
      <div className="corporate-dashboard">
        <PageLoading message="Loading corporate dashboard…" />
      </div>
    );
  }

  return (
    <div className="corporate-dashboard">
      {/* ── Header ── */}
      <div className="cd-header">
        <div className="cd-header-left">
          <h1 className="cd-title">Corporate Dashboard</h1>
          <span className="cd-subtitle">Enterprise-level OEE Performance & Analytics</span>
        </div>
        <div className="cd-filters">
          <div className="cd-date-range">
            <input type="date" className="cd-filter-input" value={dateRange.start}
              onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))} />
            <span className="cd-date-sep">to</span>
            <input type="date" className="cd-filter-input" value={dateRange.end}
              onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))} />
          </div>
          <div className="cd-plant-pills">
            {plants.map(p => (
              <button key={p.plantId}
                className={`cd-pill ${selectedPlants.includes(p.plantId) ? 'cd-pill-active' : ''}`}
                onClick={() => togglePlant(p.plantId)}>
                {p.plantName || p.plantCode}
              </button>
            ))}
            {selectedPlants.length > 0 && (
              <button className="cd-pill cd-pill-clear" onClick={() => setSelectedPlants([])}>Clear</button>
            )}
          </div>
          {/* View Toggle */}
          <div className="cd-view-toggle">
            <button className={`cd-view-btn ${viewMode === 'chart' ? 'cd-view-active' : ''}`}
              onClick={() => setViewMode('chart')} title="Chart View">
              <HiOutlineChartBar /> Charts
            </button>
            <button className={`cd-view-btn ${viewMode === 'table' ? 'cd-view-active' : ''}`}
              onClick={() => setViewMode('table')} title="Table View">
              <HiOutlineTableCells /> Tables
            </button>
          </div>
          <div className="cd-granularity">
            {['daily', 'weekly', 'monthly'].map(g => (
              <button key={g} className={`cd-gran-btn ${granularity === g ? 'cd-gran-active' : ''}`}
                onClick={() => setGranularity(g)}>
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
          <button type="button" className="cd-refresh-btn" onClick={loadDashboardData} disabled={refreshing}>
            <HiOutlineArrowPath className={refreshing ? 'cd-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="cd-kpi-row">
        <CdKpi label="Enterprise OEE" value={`${enterpriseOee}%`} color={oeeColor(enterpriseOee)} icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
          </svg>
        } large />
        <CdKpi label="Best Plant" valueStyle={{ fontSize: '16px', fontWeight: 600 }} value={bestPlant.name} subValue={`${bestPlant.oee}%`} color="#16a34a" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="greencolor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path></svg>} />
        <CdKpi label="Worst Plant" valueStyle={{ fontSize: '16px', fontWeight: 600 }} value={worstPlant.name} subValue={`${worstPlant.oee}%`} color="#ef4444" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="redcolor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>} />
        <CdKpi label="Total Downtime" valueStyle={{ fontSize: '16px', fontWeight: 600 }} value={`${totalDowntimeHrs} hrs`} color="#f59e0b" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="yellowcolor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12,6 12,12 16,14"></polyline></svg>} />
        <CdKpi label="MTBF" value={avgMtbf !== '-' ? `${avgMtbf} hrs` : '-'} color="#0ea5e9" icon={<svg viewBox="0 0 24 24" fill="none" className='bluecolor' stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"></path></svg>} />
        <CdKpi label="MTBR" value={avgMtbr !== '-' ? `${avgMtbr} hrs` : '-'} color="#8b5cf6" icon={<svg viewBox="0 0 24 24" fill="none" className='purplecolor' stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="M12 8v4l3 3"></path></svg>} />
        <CdKpi label="Open CAPAs" value={capaCount} color="#ec4899" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className='redcolor' stroke-width="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"></path><rect x="9" y="3" width="6" height="4" rx="1"></rect><line x1="9" y1="12" x2="15" y2="12"></line><line x1="9" y1="16" x2="13" y2="16"></line></svg>} />
        <CdKpi label="Plants" value={plants.length} color="#14b8a6" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className='greencolor' stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"></rect><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"></path><line x1="12" y1="12" x2="12" y2="16"></line><line x1="10" y1="14" x2="14" y2="14"></line></svg>} />
      </div>

      {/* ═══════════════ CHART VIEW ═══════════════ */}
      {viewMode === 'chart' && (
        <>
          {/* Plant OEE + Trend */}
          <div className="cd-charts-row">
            <div className="cd-chart-card cd-chart-half">
              <h3 className="cd-chart-title">Plant-wise OEE Comparison</h3>
              {plantOeeData.length === 0
                ? <div className="cd-empty">No plant data available</div>
                : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={plantOeeData} margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                      <Tooltip formatter={v => `${v}%`} />
                      <Legend />
                      <Bar dataKey="oee" name="OEE" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="availability" name="Availability" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="performance" name="Performance" fill={SEMANTIC_SUCCESS_MID} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="quality" name="Quality" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
            </div>
            <div className="cd-chart-card cd-chart-half">
              <h3 className="cd-chart-title">OEE Trend</h3>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={trendData} margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="oeeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                  <Tooltip formatter={v => `${v}%`} />
                  <Area type="monotone" dataKey="oee" stroke="#6366f1" strokeWidth={3} fill="url(#oeeGrad)" dot={{ r: 4, fill: '#6366f1' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Loss Breakdown + Top Downtime */}
          <div className="cd-charts-row">
            <div className="cd-chart-card cd-chart-half">
              <h3 className="cd-chart-title">Loss Category Breakdown</h3>
              {lossBreakdown.length === 0 ? <div className="cd-empty">No loss data</div> : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie data={lossBreakdown} cx="50%" cy="50%" innerRadius={70} outerRadius={120}
                      paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}%`}>
                      {lossBreakdown.map((entry, i) => (<Cell key={i} fill={entry.color || COLORS[i]} />))}
                    </Pie>
                    <Tooltip formatter={v => `${v}%`} /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="cd-chart-card cd-chart-half">
              <h3 className="cd-chart-title">Top Downtime Sources</h3>
              {downtimeByMachine.length === 0 ? <div className="cd-empty">No downtime data</div> : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={downtimeByMachine} layout="vertical" margin={{ left: 30, right: 20, top: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tickFormatter={v => `${v} min`} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={v => `${v} min`} />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>
                      {downtimeByMachine.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Reliability + Production */}
          <div className="cd-charts-row">
            <div className="cd-chart-card cd-chart-half">
              <h3 className="cd-chart-title">Machine Reliability (MTBF vs MTBR)</h3>
              {reliabilityData.length === 0 ? <div className="cd-empty">No reliability data</div> : (
                <ResponsiveContainer width="100%" height={320}>
                  <ScatterChart margin={{ left: 20, right: 20, top: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" dataKey="mtbf" name="MTBF (hrs)" tickFormatter={v => `${v}h`} />
                    <YAxis type="number" dataKey="mtbr" name="MTBR (hrs)" tickFormatter={v => `${v}h`} />
                    <ZAxis type="number" dataKey="size" range={[40, 200]} />
                    <Tooltip formatter={(v, name) => [`${v} hrs`, name]} />
                    <Scatter data={reliabilityData} fill="#6366f1">
                      {reliabilityData.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="cd-chart-card cd-chart-half">
              <h3 className="cd-chart-title">Plant-wise Production Summary</h3>
              {productionByPlant.length === 0 ? <div className="cd-empty">No production data</div> : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={productionByPlant} margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Legend />
                    <Bar dataKey="produced" name="Good Units" fill="#16a34a" stackId="a" />
                    <Bar dataKey="rejects" name="Rejects" fill="#ef4444" stackId="a" />
                    <Bar dataKey="rework" name="Rework" fill="#f59e0b" stackId="a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}

      {/* ═══════════════ TABLE VIEW ═══════════════ */}
      {viewMode === 'table' && (
        <>
          {/* Plant Performance Ranking */}
          <div className="cd-chart-card cd-table-full">
            <h3 className="cd-chart-title">Plant Performance Ranking</h3>
            <div className="cd-table-wrapper">
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>Rank</th><th>Plant</th><th>OEE %</th><th>Availability %</th>
                    <th>Performance %</th><th>Quality %</th><th>Produced</th><th>Rejects</th><th>Rework</th><th>Machines</th>
                  </tr>
                </thead>
                <tbody>
                  {plantOeeData.length === 0 ? (
                    <tr><td colSpan="10" className="cd-table-empty">No plant data available</td></tr>
                  ) : (
                    [...plantOeeData].sort((a, b) => b.oee - a.oee).map((p, i) => (
                      <tr key={p.plantId} className={i % 2 === 0 ? 'cd-row-even' : ''}>
                        <td className="cd-cell-rank">{i + 1}</td>
                        <td className="cd-cell-name">{p.name}</td>
                        <td style={{ color: oeeColor(p.oee), fontWeight: 700 }}>{p.oee}%</td>
                        <td>{p.availability}%</td><td>{p.performance}%</td><td>{p.quality}%</td>
                        <td>{p.produced.toLocaleString()}</td>
                        <td className="cd-cell-reject">{p.rejects}</td>
                        <td>{p.rework}</td><td>{p.machineCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Machine Metrics */}
          <div className="cd-chart-card cd-table-full">
            <h3 className="cd-chart-title">All Machine Metrics (Worst → Best)</h3>
            <div className="cd-table-wrapper">
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>#</th><th>Machine</th><th>Code</th><th>OEE %</th>
                    <th>Availability %</th><th>Performance %</th><th>Quality %</th>
                    <th>Produced</th><th>Rejects</th><th>Planned DT</th><th>Unplanned DT</th>
                  </tr>
                </thead>
                <tbody>
                  {worstMachines.length === 0 ? (
                    <tr><td colSpan="11" className="cd-table-empty">No machine data</td></tr>
                  ) : (
                    worstMachines.map((m, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'cd-row-even' : ''}>
                        <td>{i + 1}</td>
                        <td className="cd-cell-name">{m.name}</td><td>{m.code}</td>
                        <td style={{ color: oeeColor(m.oee), fontWeight: 700 }}>{m.oee}%</td>
                        <td>{m.availability}%</td><td>{m.performance}%</td><td>{m.quality}%</td>
                        <td>{m.produced}</td><td className="cd-cell-reject">{m.rejects}</td>
                        <td>{m.plannedDT} min</td><td>{m.unplannedDT} min</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Downtime Report */}
          <div className="cd-chart-card cd-table-full">
            <h3 className="cd-chart-title">Downtime Report</h3>
            <div className="cd-table-wrapper">
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Machine</th><th>Plant</th><th>Shift</th>
                    <th>Planned DT (min)</th><th>Unplanned DT (min)</th><th>Total DT (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {downtimeReport.length === 0 ? (
                    <tr><td colSpan="7" className="cd-table-empty">No downtime data</td></tr>
                  ) : (
                    downtimeReport.slice(0, 50).map((r, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'cd-row-even' : ''}>
                        <td>{r['Date']}</td><td className="cd-cell-name">{r['Machine']}</td>
                        <td>{r['Plant']}</td><td>{r['Shift']}</td>
                        <td>{r['Planned Downtime (min)']}</td>
                        <td className="cd-cell-reject">{r['Unplanned Downtime (min)']}</td>
                        <td style={{ fontWeight: 600 }}>{r['Total Downtime (min)']}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* MTBF Table */}
          <div className="cd-chart-card cd-table-full">
            <h3 className="cd-chart-title">MTBF Report</h3>
            <div className="cd-table-wrapper">
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>Machine</th><th>Machine Code</th><th>Operating Time (hrs)</th>
                    <th>Failures</th><th>MTBF (hrs)</th>
                  </tr>
                </thead>
                <tbody>
                  {mtbfData.length === 0 ? (
                    <tr><td colSpan="5" className="cd-table-empty">No MTBF data</td></tr>
                  ) : (
                    mtbfData.map((m, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'cd-row-even' : ''}>
                        <td className="cd-cell-name">{m['Machine']}</td>
                        <td>{m['Machine Code']}</td>
                        <td>{m['Total Operating Time (hours)']}</td>
                        <td>{m['Number of Failures']}</td>
                        <td style={{ fontWeight: 700, color: '#0ea5e9' }}>{m['MTBF (hours)']}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* MTBR Table */}
          <div className="cd-chart-card cd-table-full">
            <h3 className="cd-chart-title">MTBR Report</h3>
            <div className="cd-table-wrapper">
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>Machine</th><th>Machine Code</th><th>Total Downtime (hrs)</th>
                    <th>Breakdowns</th><th>MTBR (hrs)</th>
                  </tr>
                </thead>
                <tbody>
                  {mtbrData.length === 0 ? (
                    <tr><td colSpan="5" className="cd-table-empty">No MTBR data</td></tr>
                  ) : (
                    mtbrData.map((m, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'cd-row-even' : ''}>
                        <td className="cd-cell-name">{m['Machine']}</td>
                        <td>{m['Machine Code']}</td>
                        <td>{m['Total Downtime (hours)']}</td>
                        <td>{m['Number of Breakdowns']}</td>
                        <td style={{ fontWeight: 700, color: '#8b5cf6' }}>{m['MTBR (hours)']}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const CdKpi = ({ label, value, subValue, color, icon, large, valueStyle = {} }) => (
  <div className={`cd-kpi-card ${large ? 'cd-kpi-large' : ''}`}>
    <div className="cd-kpi-icon" style={{ background: `${color}18` }}><span>{icon}</span></div>
    <div className="cd-kpi-info">
      <span className="cd-kpi-value" style={{ color, ...valueStyle }}>{value}</span>
      {subValue && <span className="cd-kpi-sub">{subValue}</span>}
      <span className="cd-kpi-label">{label}</span>
    </div>
  </div>
);

export default CorporateDashboard;
